/**
 * @file GenericTooltip.tsx
 * @description
 * -----------------------------------------------------
 * Componente genérico de tooltip para reutilizar lógica de apertura y cierre
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-11-25
 * @author Correcta
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 */

"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { BUTTON_STATUS, TYPE_BUTTON } from "@/constants/button";
import { InfoIcon } from "lucide-react";
import { GenericTooltipProps } from "@/types/ui";

const GenericTooltip: React.FC<GenericTooltipProps> = ({
  title,
  trigger,
  children,
  onOpenChange,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const closeTooltip = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) return prev;
      onOpenChange?.(false);
      return false;
    });
  }, [onOpenChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        closeTooltip();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeTooltip();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeTooltip]);

  const openTooltip = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) return prev;
      onOpenChange?.(true);
      return true;
    });
  }, [onOpenChange]);

  const toggleOpen = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setIsOpen((prev) => {
        const next = !prev;
        onOpenChange?.(next);
        return next;
      });
    },
    [onOpenChange],
  );

  return (
    <article
      ref={tooltipRef}
      onBlurCapture={(event) => {
        const nextFocus = event.relatedTarget as Node | null;
        if (!nextFocus || !event.currentTarget.contains(nextFocus)) {
          closeTooltip();
        }
      }}
    >
      <section
        onClick={toggleOpen}
        onFocusCapture={openTooltip}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
      >
        {trigger ? (
          trigger
        ) : (
          <ButtonComponent type={TYPE_BUTTON} status={BUTTON_STATUS.SECONDARY}>
            <InfoIcon size={24} />
          </ButtonComponent>
        )}
      </section>

      {isOpen && (
        <section className={`profile-metrics-tooltip__dropdown`}>
          {title && <h3 className="profile-metrics-tooltip__title">{title}</h3>}
          <section>{children}</section>
        </section>
      )}
    </article>
  );
};

export default GenericTooltip;
